import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Grid, List } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface Column<T> {
  key: keyof T;
  label: string;
  render?: (value: T[keyof T], item: T) => React.ReactNode;
  className?: string;
  mobileHide?: boolean; // Hide this column in mobile card view
}

interface ResponsiveTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  className?: string;
  cardTitle?: (item: T) => React.ReactNode;
  cardDescription?: (item: T) => React.ReactNode;
  actions?: (item: T) => React.ReactNode;
  loading?: boolean;
  emptyMessage?: string;
}

export function ResponsiveTable<T extends Record<string, any>>({
  data,
  columns,
  keyField,
  className,
  cardTitle,
  cardDescription,
  actions,
  loading = false,
  emptyMessage = "No data available"
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(isMobile ? 'cards' : 'table');

  if (loading) {
    return (
      <div className={cn("space-y-4", className)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn("text-center py-8 text-muted-foreground", className)}>
        {emptyMessage}
      </div>
    );
  }

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'table' ? 'cards' : 'table');
  };

  // Table view
  if (viewMode === 'table' && !isMobile) {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleViewMode}
            className="min-h-11"
          >
            <Grid className="h-4 w-4 mr-2" />
            Card View
          </Button>
        </div>
        
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={String(column.key)} className={column.className}>
                    {column.label}
                  </TableHead>
                ))}
                {actions && <TableHead className="w-[100px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item) => (
                <TableRow key={String(item[keyField])}>
                  {columns.map((column) => (
                    <TableCell key={String(column.key)} className={column.className}>
                      {column.render 
                        ? column.render(item[column.key], item)
                        : String(item[column.key] || '')
                      }
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {actions(item)}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // Card view
  return (
    <div className={cn("space-y-4", className)}>
      {!isMobile && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleViewMode}
            className="min-h-11"
          >
            <List className="h-4 w-4 mr-2" />
            Table View
          </Button>
        </div>
      )}
      
      <div className="space-y-4">
        {data.map((item) => (
          <Card key={String(item[keyField])} className="relative">
            <CardHeader className={cn(
              "pb-4",
              actions && "pr-16" // Leave space for actions
            )}>
              {cardTitle && (
                <CardTitle className="text-base">
                  {cardTitle(item)}
                </CardTitle>
              )}
              {cardDescription && (
                <div className="text-sm text-muted-foreground">
                  {cardDescription(item)}
                </div>
              )}
              
              {actions && (
                <div className="absolute top-4 right-4 flex items-center gap-1">
                  {actions(item)}
                </div>
              )}
            </CardHeader>
            
            <CardContent className="pt-0 space-y-3">
              {columns
                .filter(column => !column.mobileHide)
                .map((column) => (
                  <div key={String(column.key)} className="flex justify-between items-start">
                    <span className="text-sm font-medium text-muted-foreground min-w-0 mr-4">
                      {column.label}:
                    </span>
                    <span className="text-sm text-right flex-1 min-w-0">
                      {column.render 
                        ? column.render(item[column.key], item)
                        : String(item[column.key] || '')
                      }
                    </span>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}